<template>
    <page-layout class="section-target">
        <template #header>
            <page-title :title="getTitle" @on-close="handleClose" />
        </template>
        <g-loader v-if="isLoading" type="fixed" />
        <template v-else-if="deliverable && deliverable.requires_approval">
            <div class="deliverable-header">
                <g-button
                    icon="angle-left"
                    class="margin-bottom-normal"
                    size="small"
                    data-testid="back-button"
                    @click="handleBack"
                >
                    Back
                </g-button>
                <g-tag :theme="CONTENT_APPROVAL_STATUS_COLORS[deliverable.approval_status]">
                    {{
                        deliverable.approval_status
                            ? CONTENT_APPROVAL_STATUS_LABELS[
                                  deliverable.is_complete ? 'completed' : deliverable.approval_status
                              ]
                            : CONTENT_APPROVAL_STATUS_LABELS[CONTENT_APPROVAL_STATUS_NONE]
                    }}
                </g-tag>
            </div>

            <g-typography tag="h4" font-weight="semibold" :value="task.name" class="g-mb-3" />

            <div class="g-mb-3">
                <activation-task-icon :task="task" />
                <g-typography tag="span" size="small" :value="getLabel(task)" />
            </div>

            {{
                [CONTENT_APPROVAL_STATUS_APPROVED, CONTENT_APPROVAL_STATUS_REJECTED].includes(
                    deliverable.approval_status
                )
                    ? brief.brand.name
                    : null
            }}
            {{ CONTENT_APPROVAL_ERROR_MESSAGES[deliverable.approval_status ? deliverable.approval_status : ''] }}

            <base-box
                v-if="
                    deliverable.last_approval_content_activity &&
                    deliverable.last_approval_content_activity.action_reason
                "
                border-radius="large"
                padding="1em"
                class="deliverable-box deliverable-feedback"
            >
                <div slot="content">
                    <g-typography tag="p">{{ deliverable.last_approval_content_activity.action_reason }}</g-typography>
                    <g-typography tag="p" class="margin-top-normal g-mt-5">
                        {{ deliverable.last_approval_content_activity.doer_name }}
                        {{ getMonth(deliverable.last_approval_content_activity.updated_at) }} at
                        {{ getTime(deliverable.last_approval_content_activity.updated_at) }}
                    </g-typography>
                </div>
            </base-box>
            <div class="deliverable-submit-section">
                <g-button
                    v-if="!deliverable.approval_status"
                    class="upload-button"
                    type="primary"
                    shape="round"
                    size="large"
                    theme="live"
                    block
                    @click="handleUploadContent"
                >
                    Upload
                </g-button>
                <g-button
                    v-if="
                        [
                            CONTENT_APPROVAL_STATUS_APPROVED,
                            CONTENT_APPROVAL_STATUS_REJECTED,
                            CONTENT_APPROVAL_STATUS_REVIEW
                        ].includes(deliverable.approval_status) &&
                        !deliverable.is_complete &&
                        completedTimelineCheck
                    "
                    class="upload-button"
                    type="transparent"
                    shape="round"
                    size="large"
                    theme="live"
                    block
                    @click="handleViewHistory"
                >
                    View History
                </g-button>
            </div>

            <g-typography v-if="deliverable.approval_status" tag="p" font-weight="semibold">
                {{ CONTENT_APPROVAL_STATUS_HEADERS[deliverable.approval_status ? deliverable.approval_status : ''] }}
            </g-typography>

            <g-typography
                v-if="deliverable.last_approval_content_activity"
                tag="p"
                class="margin-top-normal margin-bottom-normal g-mt-5"
                color="grey-6"
            >
                {{ getMonth(deliverable.last_approval_content_activity.updated_at) }} at
                {{ getTime(deliverable.last_approval_content_activity.updated_at) }}
            </g-typography>
            <activation-file
                v-if="deliverable.last_approval_content_activity"
                :file="getFile(deliverable.last_approval_content_activity.content.files)"
                :deletable="false"
                class="g-mt-3"
            />

            <base-box
                v-if="
                    deliverable.last_approval_content_activity &&
                    deliverable.last_approval_content_activity.content.description
                "
                border-radius="large"
                padding="1em"
                class="deliverable-box deliverable-description g-mt-3"
            >
                <div slot="content">
                    <div
                        class="padding-top-normal"
                        v-html="$sanitize(deliverable.last_approval_content_activity.content.description)"
                    ></div>
                </div>
            </base-box>
        </template>

        <g-button
            v-if="
                deliverable &&
                deliverable.approval_status === CONTENT_APPROVAL_STATUS_APPROVED &&
                !deliverable.is_complete
            "
            class="upload-button deliverable-button g-mt-3"
            type="primary"
            shape="round"
            size="large"
            theme="live"
            font-weight="bold"
            block
            @click="goToDeliveryAction(task)"
        >
            Complete Deliverable
        </g-button>

        <g-button
            v-if="
                deliverable &&
                deliverable.approval_status === CONTENT_APPROVAL_STATUS_REJECTED &&
                !deliverable.is_complete
            "
            class="upload-button deliverable-button g-mt-3"
            type="primary"
            shape="round"
            size="large"
            theme="live"
            font-weight="bold"
            block
            @click="handleUploadContent"
        >
            <g-icon class="edit" name="edit" size="small" />
            Edit Upload
        </g-button>
        <activation-deliverable-content-approval-upload-drawer
            :brief="brief"
            :visible="isUploadDrawerVisible"
            :is-loading="isLoading"
            :deliverable="deliverable"
            :upload-content-url="uploadContentUrl"
            :requires-caption-approval="requiresCaptionApproval"
            :upload-headers="uploadHeaders"
            :uploaded-files="uploadedFiles"
            :has-uploaded-files="hasUploadedFiles"
            :is-file-uploading="isFileUploading"
            @file-change="handleFileChange"
            @reset-uploads="handleResetUploads"
            @refresh-deliverable="handleRefreshDeliverable"
            @close="hideUploadDrawer"
        />
        <activation-deliverable-content-approval-history-drawer
            :brief="brief"
            :deliverable="deliverable"
            :visible="isHistoryDrawerVisible"
            :is-loading="isLoading"
            :requires-caption-approval="requiresCaptionApproval"
            @close="hideHistoryDrawer"
        />
    </page-layout>
</template>

<script>
    import { computed, toRefs, watch } from 'vue-demi';
    import PageLayout from '../../../../layouts/PageLayout.vue';
    import PageTitle from '../../../../library/PageTitle.vue';
    import BaseBox from '../../../../library/BaseBox.vue';
    import ActivationFile from '../../../../library/ActivationFile.vue';
    import ActivationTaskIcon from '../../partials/tasks/ActivationTaskIcon.vue';
    import {
        CONTENT_APPROVAL_ERROR_MESSAGES,
        CONTENT_APPROVAL_STATUS_HEADERS,
        CONTENT_APPROVAL_FILE_TYPE,
        BRAND_ASSET_FILE_TYPE,
        CONTENT_TYPE_FILE,
        CONTENT_APPROVAL_STATUS_REJECTED,
        CONTENT_APPROVAL_STATUS_APPROVED,
        CONTENT_APPROVAL_STATUS_NONE,
        CONTENT_APPROVAL_STATUS_REVIEW,
        CONTENT_APPROVAL_STATUS_COLORS,
        CONTENT_APPROVAL_STATUS_COMPLETED,
        CONTENT_APPROVAL_STATUS_LABELS
    } from '../../../../../constants/content-approval';
    import { getMediaLibraryFilePaths, getActivationFiles } from '../../../../../helpers/activations/activation-files';
    import { NETWORK_LABEL_MAP } from '../../../../../../../assets/app-v2/constants/networks';
    import ActivationTasksMixin from '../../../../data-views/activation-briefs/mixins/ActivationTasksMixin';
    import ActivationDeliverableContentApprovalUploadDrawer from './ActivationDeliverableContentApprovalUploadDrawer.vue';
    import ActivationDeliverableContentApprovalHistoryDrawer from './ActivationDeliverableContentApprovalHistoryDrawer.vue';
    import useToggle from '../../../../../../src/composables/use-toggle';
    import useTaskDelivery from '../../composables/use-task-delivery';
    import useEventTracking from '../../../../../../src/composables/use-event-tracking';
    import {
        EVENT_NAME_CONTENT_APPROVAL_EDIT_UPLOAD_CLICK,
        EVENT_NAME_CONTENT_APPROVAL_APPROVAL_HISTORY,
        EVENT_NAME_CONTENT_APPROVAL_COMPLETE_DELIVERABLE
    } from '../../../../../constants/analytics-events';
    import moment from 'moment';

    export default {
        name: 'ActivationDeliverableContentApprovalView',

        components: {
            ActivationTaskIcon,
            BaseBox,
            PageLayout,
            PageTitle,
            ActivationDeliverableContentApprovalUploadDrawer,
            ActivationDeliverableContentApprovalHistoryDrawer,
            ActivationFile
        },
        mixins: [ActivationTasksMixin],
        props: {
            brief: {
                type: Object,
                default: null
            },
            isLoading: {
                type: Boolean,
                default: false
            },
            deliverable: {
                type: Object,
                default: null
            },
            task: {
                type: Object,
                default: null
            },
            uploadContentUrl: {
                type: String,
                default: ''
            },
            requiresCaptionApproval: {
                type: Boolean,
                default: false
            },
            uploadHeaders: {
                type: Object,
                default: null
            },
            hasUploadedFiles: {
                type: Boolean,
                default: false
            },
            uploadedFiles: {
                type: Array,
                default: null
            },
            isFileUploading: {
                type: Boolean,
                default: false
            }
        },

        setup(props, context) {
            const events = useEventTracking();
            const activationFiles = computed(() => getActivationFiles(props.brief, BRAND_ASSET_FILE_TYPE));
            const headerMessage = computed(
                () => `(${props.deliverable?.index ? props.deliverable.index : ''} of ${props.task?.quantity})`
            );
            const getTitle = computed(() => `Deliverable ${props.deliverable?.index ? props.deliverable.index : ''}`);
            const {
                currentValue: isUploadDrawerVisible,
                setTrue: showUploadDrawer,
                setFalse: hideUploadDrawer
            } = useToggle(false);

            const {
                currentValue: isHistoryDrawerVisible,
                setTrue: showHistoryDrawer,
                setFalse: hideHistoryDrawer
            } = useToggle(false);

            const handleUploadContent = () => {
                if (props.deliverable.approval_status === CONTENT_APPROVAL_STATUS_REJECTED) {
                    events.trackEvent(
                        EVENT_NAME_CONTENT_APPROVAL_EDIT_UPLOAD_CLICK,
                        getEditUploadEventPayload(),
                        false
                    );
                }
                showUploadDrawer();
            };

            const handleClose = () => {
                context.emit('close');
            };

            const handleBack = () => {
                context.emit('back');
            };

            const handleFileChange = (info) => {
                context.emit('file-change', info);
            };

            const handleResetUploads = () => {
                context.emit('reset-uploads');
            };

            const handleRefreshDeliverable = () => {
                context.emit('refresh-deliverable');
            };

            const handleViewHistory = () => {
                events.trackEvent(EVENT_NAME_CONTENT_APPROVAL_APPROVAL_HISTORY, getHistoryEventPayload);
                showHistoryDrawer();
            };

            const getFile = (files) => {
                return getMediaLibraryFilePaths(files, CONTENT_APPROVAL_FILE_TYPE)[0];
            };

            const { goToDeliveryPage } = useTaskDelivery();

            const goToDeliveryAction = (taskSelection) => {
                events.trackEvent(EVENT_NAME_CONTENT_APPROVAL_COMPLETE_DELIVERABLE, getHistoryEventPayload);
                goToDeliveryPage(taskSelection, true);
            };

            const getMonth = (jsDate) => {
                const date = new Date(jsDate);
                return moment(jsDate).format('MMM D YYYY');
            };

            const getTime = (jsDate) => {
                const date = new Date(jsDate);
                return moment(jsDate).format('h:mm a');
            };

            const completedTimelineCheck = computed(() => {
                if (props.deliverable.is_complete && !props.deliverable.approval_content_activity?.length) {
                    return false;
                } else {
                    return true;
                }
            });

            const { deliverable, isLoading } = toRefs(props);
            watch(isLoading, () => {
                if (isLoading.value === false && deliverable.value && !deliverable.value?.approval_status) {
                    handleUploadContent();
                }
            });

            const getEditUploadEventPayload = () => {
                return {
                    activation_id: props.deliverable.activation_id,
                    contact_id: props.brief.contact_id
                };
            };

            const getHistoryEventPayload = () => {
                return {
                    activation_id: props.deliverable.activation_id,
                    contact_id: props.brief.contact_id
                };
            };

            return {
                BaseBox,
                activationFiles,
                isUploadDrawerVisible,
                hideUploadDrawer,
                handleUploadContent,
                handleFileChange,
                handleClose,
                handleBack,
                handleResetUploads,
                handleRefreshDeliverable,
                handleViewHistory,
                hideHistoryDrawer,
                getFile,
                getMonth,
                getTime,
                getTitle,
                goToDeliveryPage,
                goToDeliveryAction,
                completedTimelineCheck,
                isHistoryDrawerVisible,
                headerMessage,
                BRAND_ASSET_FILE_TYPE,
                CONTENT_APPROVAL_ERROR_MESSAGES,
                CONTENT_TYPE_FILE,
                CONTENT_APPROVAL_STATUS_NONE,
                CONTENT_APPROVAL_STATUS_APPROVED,
                CONTENT_APPROVAL_STATUS_REVIEW,
                CONTENT_APPROVAL_STATUS_COLORS,
                CONTENT_APPROVAL_STATUS_REJECTED,
                CONTENT_APPROVAL_STATUS_LABELS,
                CONTENT_APPROVAL_STATUS_COMPLETED,
                CONTENT_APPROVAL_STATUS_HEADERS,
                NETWORK_LABEL_MAP
            };
        }
    };
</script>

<style lang="scss" scoped>
    .deliverable-box {
        padding: 1em;
        margin-top: 1em;
    }
    .g-header {
        &.g-header-padding {
            padding: 0 !important;
        }
    }
    .upload-button {
        margin: 0;
        margin-top: 1em;
    }
    .campaign-view-image {
        margin-top: 1em;
        margin-bottom: 1em;
    }
    .deliverable-header {
        display: flex;
        align-content: center;
        justify-content: space-between;
        margin-bottom: $g-spacing-6;
    }
    .deliverable-submit-section {
        margin-bottom: 1em;
        padding-bottom: 1em;
        border-bottom: solid 1px $g-color-grey-4;
    }
    .overview-section {
        margin-top: 1em;
    }
    .brand-assets {
        margin-top: 1em;
    }
    .fa-clock {
        color: $g-color-primary-6;
    }
    .fa-check-circle {
        color: $g-color-green-7;
    }
    .fa-exclamation-circle {
        color: $g-color-orange-4;
    }
    .brand-assets {
        padding: 1em;
        border-color: $g-color-grey-3;
        border-radius: 6px;
        border-style: solid;

        .g-border-solid {
            border-style: unset;
        }
    }
    .tag-text {
        font-weight: normal;
    }
    .tag-header {
        display: flex;
    }
    .status-box {
        background: $g-color-grey-2;
    }
    .deliverable-description {
        background: $g-color-grey-2;
        box-shadow: none;
        border: none;
    }
    .deliverable-content-section {
        height: 90%;
    }
    .deliverable-feedback {
        background: $g-color-grey-2;
    }
    .deliverable-button {
        position: absolute;
        bottom: 3em;
        width: 90%;

        @media (max-width: 400px) {
            width: 83%;
        }
    }
    .section-target::v-deep {
        .g-content {
            height: 90%;
        }
    }
</style>
